import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SEO_ROUTES_MAP } from 'constants/seoRoutes';

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
}

// Helpers
const envBool = (value?: string) => value === 'true';

// SEO global flag
const isSeoEnabled = envBool(import.meta.env.VITE_SEO_ENABLE);

const SEO = ({
  title,
  description,
  canonicalUrl,
  noIndex = false,
}: SEOProps) => {
  const location = useLocation();
  const siteTitle = 'Благо';

  // Route-based SEO config
  const routeConfig = SEO_ROUTES_MAP[location.pathname];

  // Final title & description
  const pageTitle = title ?? routeConfig?.title ?? '';
  const pageDescription =
    description ?? routeConfig?.description ?? '';

  const fullTitle = pageTitle
    ? `${pageTitle} | ${siteTitle}`
    : siteTitle;

  /**
   * noindex logic:
   * - SEO globally disabled
   * - noindex from props
   * - noindex from route config
   */
  const shouldNoIndex =
    !isSeoEnabled ||
    noIndex ||
    routeConfig?.noIndex;

  return (
    <Helmet>
      {/* Basic SEO */}
      <title>{fullTitle}</title>
      {pageDescription && (
        <meta name="description" content={pageDescription} />
      )}

      {/* Indexing rules */}
      {shouldNoIndex && (
        <meta name="robots" content="noindex, nofollow" />
      )}

      {/* Canonical */}
      {canonicalUrl && (
        <link rel="canonical" href={canonicalUrl} />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {pageDescription && (
        <meta
          property="og:description"
          content={pageDescription}
        />
      )}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteTitle} />
    </Helmet>
  );
};

export default SEO;
